<template>
  <div>
    <b-card>
      <b-row>
        <b-col>
          <b-form-group label="Filter:">
            <b-input-group>
              <b-form-input v-model="filter" placeholder="Type to search..." @input="onFilterChange"></b-form-input>
              <b-input-group-append v-if="filter">
                <b-button @click="filter = ''" variant="outline-secondary">
                  <b-icon icon="x-circle-fill"></b-icon>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>

          <template>
            <div>
            <!-- Tabella con checkbox -->
              <b-table ref="selectableTable" :items="paginatedPods" :fields="podFields" responsive hover striped>
                <template #head(select)>
                  <div v-if="paginatedPods.length > 0">
                    <b-form-checkbox v-model="selectAll" @change="toggleSelectAll"></b-form-checkbox>
                  </div>
                </template>
                <template #cell(select)="{ item }">
                  <b-form-checkbox
                    v-model="localSelections[item.pod]"
                    :disabled="alreadySelectedPods.has(item.pod)"
                  />
                </template>
              </b-table>
            </div>
          </template>

          <!-- Add Pods button -->
          <b-button
            @click="addSelectedPods"
            variant="primary"
            class="mt-2 mb-3"
            :disabled="!hasNewSelections"
          >
            Add Selected PODs
          </b-button>

          <div class="d-flex justify-content-between align-items-center">
            <b-pagination
              v-if="totalRows > perPage"
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              class="mb-0"
            ></b-pagination>
            <b-form-select
              v-model="perPage"
              :options="[5, 10, 15, 20]"
              size="sm"
              style="width: 100px"
            ></b-form-select>
          </div>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
export default {
  name: 'PodSelector',

  props: {
    availablePods: {
      type: Array,
      required: true
    },
    selectedPodsList: {
      type: Array,
      default: () => []
    },
    alreadySelectedPods: {
      type: Set,
      default: () => new Set()
    }
  },

  data () {
    return {
      filter: '',
      currentPage: 1,
      perPage: 10,
      podFields: [
        { key: 'select', label: '' },
        { key: "pod", label: "POD" },
        { key: "baselineId", label: "Baseline ID" },
        { key: "aggregatorId", label: "Aggregator ID" },
        { key: "upperP", label: "upperP (kW)" },
        { key: "upperQ", label: "upperQ (kW)" }
      ],
      selectedPods: new Set(),
      localAvailablePods: [],
      localSelections: {}
    }
  },

  computed: {
    filteredPods () {
      if (!this.filter) return this.localAvailablePods

      const filterLower = this.filter.toLowerCase()
      return this.localAvailablePods.filter(pod => {
        const podMatch = pod.pod && pod.pod.toLowerCase().includes(filterLower)
        const aggregatorMatch = pod.aggregatorId && pod.aggregatorId.toLowerCase().includes(filterLower)
        return podMatch || aggregatorMatch
      })
    },

    totalRows () {
      return this.filteredPods.length
    },

    paginatedPods () {
      const start = (this.currentPage - 1) * this.perPage
      const end = start + this.perPage
      return this.filteredPods.slice(start, end)
    },

    hasNewSelections () {
      return Object.values(this.localSelections).some(selected => selected)
    }
  },

  watch: {
    availablePods: {
      immediate: true,
      handler (newPods) {
        this.localAvailablePods = newPods

        newPods.forEach(pod => {
          if (this.selectedPods.has(pod.pod)) {
            this.$set(this.localSelections, pod.pod, true)
          } else if (!this.localSelections.hasOwnProperty(pod.pod)) {
            this.$set(this.localSelections, pod.pod, false)
          }
        })
      }
    },

    selectedPodsList: {
      immediate: true,
      handler (newList) {
        this.selectedPods = new Set(newList.map(pod => pod.pod))

        newList.forEach(pod => {
          this.$set(this.localSelections, pod.pod, true)
        })
      }
    }
  },

  methods: {
    onFilterChange () {
      this.currentPage = 1
    },

    addSelectedPods () {
      const newlySelectedPods = this.availablePods.filter(pod =>
        this.localSelections[pod.pod] && !this.selectedPods.has(pod.pod)
      )

      if (newlySelectedPods.length > 0) {
        newlySelectedPods.forEach(pod => {
          this.selectedPods.add(pod.pod)

          this.$emit('pod-permanently-selected', pod.pod)
        })

        this.$emit('pods-selected', newlySelectedPods)

        newlySelectedPods.forEach(pod => {
          this.$set(this.localSelections, pod.pod, false)
        })

        this.localAvailablePods = this.localAvailablePods.filter(
          pod => !newlySelectedPods.some(selectedPod => selectedPod.pod === pod.pod)
        )

        this.$nextTick(() => {
          if (this.$refs.selectableTable) {
            this.$refs.selectableTable.refresh()
          }
        })

        this.$bvToast.toast(`Successfully added ${newlySelectedPods.length} POD(s)`, {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      }
    },

    unselectPod (podId) {
      this.selectedPods.delete(podId)
      this.$set(this.localSelections, podId, false)

      const podToRestore = this.availablePods.find(p => p.pod === podId)
      if (podToRestore) {
        this.$emit('pod-restored', podToRestore)
      }

      const selectedPods = this.availablePods.filter(pod => this.selectedPods.has(pod.pod))
      this.$emit('pods-selected', selectedPods)
    },

    updateAvailablePods (newPods) {
      this.localAvailablePods = newPods.filter(pod => !this.selectedPods.has(pod.pod))

      Object.keys(this.localSelections).forEach(podId => {
        if (!this.localAvailablePods.some(p => p.pod === podId)) {
          this.$delete(this.localSelections, podId)
        }
      })

      this.localAvailablePods.forEach(pod => {
        if (!this.localSelections.hasOwnProperty(pod.pod)) {
          this.$set(this.localSelections, pod.pod, false)
        }
      })

      this.$nextTick(() => {
        if (this.$refs.selectableTable) {
          this.$refs.selectableTable.refresh()
        }
      })
    },
    toggleSelectAll () {
      if (this.selectAll) {
        this.paginatedPods.forEach(pod => {
          if (!this.alreadySelectedPods.has(pod.pod)) {
            this.$set(this.localSelections, pod.pod, true)
          }
        })
      } else {
        this.paginatedPods.forEach(pod => {
          if (!this.alreadySelectedPods.has(pod.pod)) {
            this.$set(this.localSelections, pod.pod, false)
          }
        })
      }
    }
  }
}

</script>

<style scoped>
.table >>> thead {
  background-color: #af0f6a;
  color: white;
}

.table >>> th,
.table >>> td {
  vertical-align: middle;
}

.btn-primary {
  background-color: #af0f6a;
  border-color: #af0f6a;
}

.btn-primary:hover, .btn-primary:focus, .btn-primary:active {
  background-color: #8f0d57;
  border-color: #8f0d57;
}

.btn-primary:disabled {
  background-color: #d4d4d4;
  border-color: #d4d4d4;
}
</style>



// WEBPACK FOOTER //
// src/components/PodSelector.vue